import FormLabel from '@c/forms/controls/FormLabel';
import { SelectOption } from '@c/forms/controls/FormSelect';
import { CloseIcon } from '@c/icons';
import PageSection from '@c/listing/shared/PageSection';
import Button from '@ui/Button';
import Input from '@ui/Input';
import Select from '@ui/Select';
import useBikes from '@util/hooks/useBikes';
import { Bike, Make } from 'models/shared';
import { useEffect, useState } from 'react';
import { useCreateListing } from '../_context';
import SafeImage from '@ui/SafeImage';
import { prefix } from '@util/imagePrefix';

export const getMakeLogo = (prefix: string, name: string) =>
  `${prefix}/assets/brands/${name
    .toLowerCase()
    .split(' ')
    .join('-')}_1920x2560.png`;

export const BikeFitment = () => {
  const { form, product, categoryInfo } = useCreateListing();

  const initialYears = Array.from(
    new Set(product.fitting_bikes?.bikes.map((bike) => `${bike.year}`) ?? [])
  );
  const initialMakes = Array.from(
    new Set(product.fitting_bikes?.bikes.map((bike) => `${bike.make}`) ?? [])
  );
  const initialModels = Array.from(
    new Set(product.fitting_bikes?.bikes.map((bike) => `${bike.model}`) ?? [])
  );

  const [years, setYears] = useState<string[]>(initialYears);
  const [makes, setMakes] = useState<string[]>(initialMakes);
  const [models, setModels] = useState<string[]>(initialModels);
  const [selectionTouched, setSelectionTouched] = useState(false);
  const { bikeYears, bikeMakes, bikeModelsByMake } = useBikes(
    years.map((y) => `${y}`),
    makes as Make[],
    product.ride_types.length
      ? (product.ride_types.filter((r) =>
          ['Dirt Bikes', 'ATV', 'UTV', 'Street', 'E-Moto'].includes(r)
        ) as ('Dirt Bikes' | 'ATV' | 'UTV' | 'Street' | 'E-Moto')[])
      : ['Dirt Bikes']
  );
  const [filter, setFilter] = useState('');

  // Derives fitting_bikes.bikes from the selected years × makes × models,
  // keeping only combos where the model exists for that make and year.
  useEffect(() => {
    if (!selectionTouched) return;
    const bikes: Bike[] = [];
    makes.forEach((make) => {
      const makeModels = bikeModelsByMake[make as Make] ?? [];
      models.forEach((model) => {
        const entry = makeModels.find((m) => m.model === model);
        if (!entry) return;
        years.forEach((year) => {
          if (!entry.years.includes(+year)) return;
          bikes.push({ make: make as Make, model, year: +year });
        });
      });
    });
    const current = form.getValues('fitting_bikes');
    const next = { bikes, universal: current?.universal ?? false };
    // bikeModelsByMake changes identity every render; bail out when nothing
    // changed to avoid a setValue → render loop.
    if (JSON.stringify(current) === JSON.stringify(next)) return;
    form.setValue('fitting_bikes', next);
  }, [selectionTouched, years, makes, models, bikeModelsByMake, form]);

  const isUniversal = product.fitting_bikes?.universal;
  const hasBikes = !!product.fitting_bikes?.bikes.length;

  const handleSwitchChange = () => {
    form.setValue('fitting_bikes.universal', !isUniversal);
  };
  const shouldShowSwitch =
    categoryInfo.every((c) => !c.is_universal_disabled) ||
    product.fitting_bikes?.universal;

  return (
    <PageSection
      title="Vehicle Fitment"
      error={form.formState.errors.fitting_bikes?.message}
      switchTitle="Fits all bikes"
      switchEnabled={isUniversal}
      showSwitch={shouldShowSwitch}
      switchOnChange={handleSwitchChange}
      className="sm:w-[110rem]"
      headerClassName="sm:w-[110rem]"
    >
      {!isUniversal && (
        <>
          {hasBikes && (
            <Button
              width="fixed"
              text="Reset fitment"
              type="tertiary"
              className="!rounded-2xl"
              onClick={() => {
                form.setValue('fitting_bikes.bikes', []);
                setYears([]);
                setMakes([]);
                setModels([]);
                setSelectionTouched(true);
              }}
            />
          )}

          <div className="flex flex-col items-center gap-8 py-16 sm:flex-row">
            <FormLabel value="Select Year(s)">
              <Select
                allowEmpty={false}
                multiple
                options={bikeYears.map((year) => ({
                  label: `${year}`,
                  value: year,
                  id: `${year}`,
                }))}
                onChange={(option: any) => {
                  setYears(option.map((o: SelectOption) => o.value));
                  setSelectionTouched(true);
                }}
                value={years.join(',')}
              />
            </FormLabel>

            <FormLabel value="Select Make(s)">
              <Select
                allowEmpty={false}
                multiple
                options={bikeMakes.map((make) => ({
                  label: make,
                  value: make,
                  id: make,
                }))}
                onChange={(option: any) => {
                  setMakes(option.map((o: SelectOption) => o.value));
                  setSelectionTouched(true);
                }}
                value={makes.join(',')}
              />
            </FormLabel>
          </div>

          <FormLabel value="Model(s)">
            {!years.length || !makes.length ? (
              <span>Choose a year and make</span>
            ) : (
              <>
                <Input
                  type="search"
                  placeholder="Filter Models..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="!rounded-2xl"
                />

                {Object.keys(bikeModelsByMake).map((make) => (
                  <div key={make} className="flex flex-col gap-4 py-6">
                    <SafeImage
                      src={getMakeLogo(prefix, make)}
                      alt={make}
                      className="mt-4 h-[4rem] w-[15rem] object-contain object-left"
                      fallback={
                        <span className="flex h-[6rem] items-center py-6 text-[3rem] font-bold">
                          {make}
                        </span>
                      }
                    />

                    <div className="mt-8 flex flex-wrap gap-4">
                      {bikeModelsByMake[make as Make]
                        .filter(
                          ({ model }) =>
                            !filter ||
                            model
                              .toLowerCase()
                              .includes(filter.toLowerCase().trim())
                        )
                        .map(({ model }) => (
                          <button
                            key={model}
                            className="rounded-full border-[1px] px-4 py-2 text-[1.5rem]"
                            style={{
                              borderColor: models.includes(model)
                                ? '#C41719'
                                : '#D9D9D9',
                              color: models.includes(model)
                                ? '#C41719'
                                : '#000',
                              backgroundColor: models.includes(model)
                                ? '#F9E8E8'
                                : '#fff',
                            }}
                            type="button"
                            onClick={() => {
                              setModels(
                                models.includes(model)
                                  ? models.filter((m) => m !== model)
                                  : [...models, model]
                              );
                              setSelectionTouched(true);
                            }}
                          >
                            {model}
                          </button>
                        ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </FormLabel>
        </>
      )}
      <div className="py-8">
        <div className="flex w-full flex-col items-start gap-x-4 gap-y-4 rounded-[1.5rem] bg-[#F9E8E7] p-6 sm:flex-row">
          <div className="hidden sm:block">
            <RedMXLogo className={'h-16 w-16'} />
          </div>
          <p className="text-[1.6rem] text-brand-secondary">
            As a seller, you assume full responsibility for accurately
            representing and selecting part fitment data. Please ensure all
            details provided are correct to avoid potential issues with buyers.
          </p>
        </div>
      </div>
    </PageSection>
  );
};

const RedMXLogo = ({ className }: { className?: string }) => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M3.24138 4L0 15.3236L2.76155 15.3342L5.91768 4H3.24138Z"
        fill="#C41719"
      />
      <path
        d="M10.4077 4.01066L7.27293 7.45465L6.18534 4L4.98047 8.05175L5.89743 12.0928L9.24547 8.06241L10.4077 4.01066Z"
        fill="#C41719"
      />
      <path
        d="M10.1366 15.3547L13.3567 4.02043L10.5844 4.00977L7.375 15.344L10.1366 15.3547Z"
        fill="#C41719"
      />
      <path
        d="M16.2832 15.3664L19.418 15.3771L18.0105 9.35274L22.4036 4.04281H19.7699L17.4134 6.93235L16.7524 4.03215L13.6389 4.02148L14.9611 9.69394L6.48438 19.7593L9.16068 19.77L15.5369 12.2529L16.2832 15.3664Z"
        fill="#C41719"
      />
      <path
        d="M13.1658 16.0059H12.6967L11.9609 18.6182H13.6563L13.7522 18.2343L12.5368 18.213L13.1658 16.0059Z"
        fill="#C41719"
      />
      <path
        d="M15.2597 18.0115L15.0465 18.1927H14.4494L14.3214 18.0328L14.7159 16.604L14.9291 16.4441H15.5049L15.6542 16.604L15.2597 18.0115ZM15.8462 16.0176H14.8545L14.3107 16.4121L13.8203 18.2141L14.1508 18.6512H15.0998L15.6649 18.2034L16.166 16.4334L15.8462 16.0176Z"
        fill="#C41719"
      />
      <path
        d="M17.2935 17.6596L17.1976 18.0541L16.995 18.2034H16.4512L16.3232 18.0008L16.7177 16.6467L16.9416 16.4547H17.4961L17.5921 16.636L17.5174 16.9559H17.9866L18.1252 16.4014L17.8159 16.0176H16.8776L16.2912 16.4654L15.8008 18.1714L16.1633 18.6512H17.0696L17.624 18.2141L17.7626 17.6703L17.2935 17.6596Z"
        fill="#C41719"
      />
      <path
        d="M20.3027 16.0273H19.8016L18.6074 17.0723L18.8952 16.0273H18.4261L17.6797 18.6503L18.1702 18.661L18.4367 17.744L18.8739 17.3602L19.1192 18.661H19.5883L19.2578 16.987L20.3027 16.0273Z"
        fill="#C41719"
      />
      <path
        d="M20.5369 17.4668H21.4112L21.4965 17.1043H20.6222L20.7928 16.4645H22.0617L22.1789 16.038L20.4836 16.0273L19.7266 18.661H21.4432L21.5605 18.2238H20.3237L20.5369 17.4668Z"
        fill="#C41719"
      />
      <path
        d="M22.4944 17.1675L22.6863 16.4851H23.3687L23.4753 16.613L23.3901 17.0182L23.1981 17.1888L22.4944 17.1675ZM23.8059 17.1675L23.9978 16.4211L23.7205 16.0586H22.3665L21.6094 18.6922H22.0786L22.3772 17.626H22.697L22.8676 18.6922H23.3474L23.1555 17.626H23.2835L23.8059 17.1675Z"
        fill="#C41719"
      />
    </svg>
  );
};
